// src/client/components/WorkspaceList/WorkspaceCard.tsx
import { Text, TextInput, UnstyledButton } from '@mantine/core';
import cx from 'clsx';
import { useEffect, useRef, useState } from 'react';

type WorkspaceCardProps = {
  readonly canRename?: boolean;
  readonly isSelected: boolean;
  readonly onRename: (newTitle: string) => void;
  readonly onSelect: () => void;
  readonly processCount: number;
  readonly title: string;
};

export const WorkspaceCard = ({
  canRename = true,
  isSelected,
  onRename,
  onSelect,
  processCount,
  title,
}: WorkspaceCardProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(title);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing) {
      inputRef.current?.select();
    }
  }, [isEditing]);

  const saveTitle = () => {
    const trimmed = editValue.trim();
    if (trimmed && trimmed !== title) {
      onRename(trimmed);
    } else {
      setEditValue(title);
    }

    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <div className="mb-1 px-1">
        <TextInput
          autoFocus
          onBlur={saveTitle}
          onChange={(event) => setEditValue(event.currentTarget.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              saveTitle();
            }

            if (event.key === 'Escape') {
              setEditValue(title);
              setIsEditing(false);
            }
          }}
          ref={inputRef}
          size="xs"
          value={editValue}
        />
      </div>
    );
  }

  return (
    <UnstyledButton
      className={cx(
        'text-left p-2.5 mb-1',
        isSelected
          ? 'w-full md:w-[calc(100%+0.5rem+10px)] bg-[#f0ece7] dark:bg-[#22262b] border border-black/15 dark:border-white/15 rounded-md md:rounded-l-md md:border-r-0 md:relative md:pr-[calc(1.25rem+9px)]'
          : 'w-full rounded-md hover:bg-black/5 dark:hover:bg-white/5',
      )}
      onClick={onSelect}
      onDoubleClick={() => {
        if (!canRename) {
          return;
        }

        setEditValue(title);
        setIsEditing(true);
      }}
    >
      <div className="flex items-center justify-between">
        <Text className="text-sm font-semibold truncate">{title}</Text>
        <Text className="text-xs text-black/40 dark:text-white/40">
          {processCount}
        </Text>
      </div>
    </UnstyledButton>
  );
};
